import {
  createApi,
  FetchArgs,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/dist/query/react';
import { HYDRATE } from 'next-redux-wrapper';
import {
  AddParticipantSchema,
  EventDataResponse,
  EventId,
  ParticipantId,
} from './types';
import getAppConfig from './appConfig';
import { sleep } from '../utils/sleep';

const is4xxStatus = (status: number) => Math.floor(status / 100) === 4;
const maxDelay = 15000;
const delayBase = 600;

const baseQuery: ReturnType<typeof fetchBaseQuery> = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    const { participantGatewayApiHost: baseUrl } = getAppConfig();

    const result = await fetchBaseQuery({
      baseUrl,
      mode: 'cors',
      credentials: 'include',
    })(args, api, extraOptions);

    if (
      result &&
      'error' in result &&
      result.error &&
      'status' in result.error &&
      typeof result.error.status === 'number' &&
      is4xxStatus(result.error.status)
    ) {
      retry.fail(result.error);
    }

    return result;
  },
  {
    maxRetries: 8,
    backoff: (attempt) =>
      sleep(
        Math.min(maxDelay, delayBase * 2 ** attempt * (Math.random() + 0.4)),
      ),
  },
);

export const participantBaseApi = createApi({
  reducerPath: 'participants',
  baseQuery,
  endpoints: () => ({}),
  // eslint-disable-next-line consistent-return
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === HYDRATE) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
      return action.payload[reducerPath];
    }
  },
});

export const participantApi = participantBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    addParticipant: builder.mutation<
      ParticipantId,
      { eventId: EventId; participant: AddParticipantSchema }
    >({
      query: ({ eventId, participant: { email, firstName, lastName } }) => ({
        url: `events/${eventId}/participants`,
        method: 'POST',
        body: {
          email,
          given_name: firstName,
          family_name: lastName,
          phone_number: '',
        },
      }),
    }),
    getEventData: builder.query<EventDataResponse, EventId>({
      query: (eventId) => `events/${eventId}`,
    }),
  }),
});

export const { useAddParticipantMutation, useGetEventDataQuery } =
  participantApi;
